import type { Breadcrumb, IBreadcrumbListParams, IPredicate } from '@feathr/blackbox';

import type { IDonationCreatedEvent } from '../../hooks';

/**
 * Predicate for filtering breadcrumbs to only donation activities.
 */
export const DONATION_PREDICATE: IPredicate = {
  kind: 'activity',
  attr_against: 'flvr',
  attr_type: 'string',
  comparison: 'eq',
  value: 'donation',
};

/**
 * Default parameters for fetching historical donation breadcrumbs.
 */
export const DONATION_LIST_PARAMS: Partial<IBreadcrumbListParams> = {
  mode: 'match_all',
  lookback_mode: 'unbounded',
  lookback_value: 0,
  predicates: [DONATION_PREDICATE],
};

interface IPersonData {
  name: string;
  isPending: boolean;
  isErrored: boolean;
}

/**
 * Parse donor name into first and last name components.
 *
 * @param fullName - The full name string from person record
 * @returns Object with first_name and last_name
 */
function parsePersonName(fullName: string): { first_name: string; last_name: string } {
  if (!fullName || fullName === 'Anonymous') {
    return { first_name: 'Anonymous', last_name: '' };
  }
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 1) {
    return { first_name: parts[0], last_name: '' };
  }
  return {
    first_name: parts[0],
    last_name: parts.slice(1).join(' '),
  };
}

/**
 * Parse payment method from custom_data.
 *
 * @param customData - The custom_data object from breadcrumb
 * @returns Payment method as 'credit_card' or 'ach'
 */
function parsePaymentMethod(
  customData: Record<string, unknown> | undefined,
): 'credit_card' | 'ach' {
  const paymentMethod = customData?.['Donation: Payment Method'];
  if (typeof paymentMethod === 'string' && paymentMethod.toLowerCase() === 'ach') {
    return 'ach';
  }
  return 'credit_card';
}

/**
 * Map donation frequency from breadcrumb format to event format.
 *
 * @param frequency - Frequency from breadcrumb ('one_time' or 'monthly')
 * @returns Frequency in event format ('one_time' or 'recurring')
 */
function mapDonationFrequency(frequency: string | undefined): 'one_time' | 'recurring' {
  return frequency === 'monthly' ? 'recurring' : 'one_time';
}

/**
 * Transform a Breadcrumb donation record into an IDonationCreatedEvent.
 *
 * @param breadcrumb - The Breadcrumb model instance
 * @param personData - Person data with name, pending, and error states
 * @param formName - Optional form name (if known)
 * @returns Transformed donation event object
 *
 * @example
 * ```ts
 * const personData = getPersonName(breadcrumb.get('per_id'));
 * const event = breadcrumbToTransactionEvent(breadcrumb, personData);
 * ```
 */
export function breadcrumbToTransactionEvent(
  breadcrumb: Breadcrumb,
  personData: IPersonData,
  formName?: string,
): IDonationCreatedEvent {
  const donation = breadcrumb.get('donation');
  const customData = breadcrumb.get('custom_data');
  const { first_name, last_name } = parsePersonName(personData.name);

  return {
    id: breadcrumb.get('id'),
    person_id: breadcrumb.get('per_id') || undefined,
    donor_first_name: first_name,
    donor_last_name: last_name,
    amount: donation?.amount ?? 0,
    credits_earned: breadcrumb.get('growth_credits') ?? 0,
    donation_type: mapDonationFrequency(donation?.frequency),
    form_id: breadcrumb.get('form_id') || '',
    form_name: formName || 'Unknown Form',
    payment_method: parsePaymentMethod(customData),
    transaction_id: breadcrumb.get('id'),
    created_at: breadcrumb.get('d_c'),
  };
}

export type { IPersonData };
